import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const stories = [
  {
  industry: 'FinTech',
  challenge: 'Legacy ERP migration was failing after 18 months with a previous vendor.',
  outcome: 'Delivered working migration in 6 weeks. Costs dropped 35%.',
  stat: '35%',
  statLabel: 'Cost Reduction',
  link: '/case-studies/fintech-erp-migration',
  },
  {
  industry: 'Healthcare',
  challenge: 'Patient data systems were siloed across three platforms with no interoperability.',
  outcome: 'Unified data platform delivered. Achieved compliance ahead of schedule.',
  stat: '3',
  statLabel: 'Platforms Unified',
  link: '/case-studies/healthcare-data-platform',
  },
  {
  industry: 'Manufacturing',
  challenge: 'Needed a real-time production monitoring system to reduce downtime.',
  outcome: 'Delivered MVP in under 4 weeks. Reduced infrastructure costs significantly.',
  stat: '<4',
  statLabel: 'Weeks to MVP',
  link: '/case-studies/manufacturing-monitoring',
  },
];

export default function SuccessStories() {
  return (
  <section data-testid="success-stories-section" className="bg-white py-16 lg:py-20">
  <div className="max-w-content mx-auto px-6">
  {/* Header */}
  <motion.div
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.5 }}
  className="mb-14"
  >
  <p className="text-overline text-orange-500 mb-3">CASE STUDIES</p>
  <h2 data-testid="success-stories-heading" className="text-h2 text-navy-800 font-black tracking-tight mb-4">Real Work. Real Results.</h2>
  <p className="text-navy-500 text-lg max-w-xl">Every transformation tells a story. Here is what happens when organizations choose to build and protect with Flynaut.</p>
  </motion.div>

  {/* Cards Grid - High-contrast editorial */}
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
  {stories.map((story, i) => (
  <motion.a
  key={story.industry}
  href={story.link}
  data-testid={`story-card-${i}`}
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.4, delay: i * 0.1 }}
  className="group bg-navy-800 rounded-xl p-8 hover:-translate-y-1 transition-all duration-200 cursor-pointer relative overflow-hidden"
  >
  {/* Accent top-left corner */}
  <div className="absolute top-0 left-0 w-16 h-1 bg-orange-500" />

  {/* Stat highlight */}
  <div className="mb-6">
  <span className="font-display font-black text-4xl text-orange-500">{story.stat}</span>
  <span className="text-white/40 text-xs uppercase tracking-wider ml-2">{story.statLabel}</span>
  </div>

  <span className="text-overline text-orange-500/70 mb-3 block">{story.industry}</span>
  <p className="text-white font-medium text-base mb-3 leading-snug">{story.challenge}</p>
  <p className="text-white/50 text-sm mb-6 leading-relaxed">{story.outcome}</p>

  <span className="text-orange-500 text-sm font-medium inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
  Read the Story <ArrowRight size={13} />
  </span>
  </motion.a>
  ))}
  </div>

  {/* View All */}
  <motion.div
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true }}
  >
  <a
  href="/case-studies"
  data-testid="view-all-cases-link"
  className="group text-orange-500 text-base font-medium inline-flex items-center gap-1.5 hover:gap-2.5 transition-all"
  >
  View All Case Studies <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
  </motion.div>
  </div>
  </section>
  );
}
